import React, { useMemo, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const MODES = ['Day', 'Dusk', 'Night'];
const MAX_NUMBER = 1e9;
const bounded = (items, maximum) => Array.isArray(items) ? items.slice(0, maximum) : [];
const finite = value => typeof value === 'number' && Number.isFinite(value);
const chartFinite = value => finite(value) && Math.abs(value) <= MAX_NUMBER;
const printable = (value, maximum = 100) => typeof value === 'string' ? value.slice(0, maximum) : '';
const numeric = value => finite(value) ? value : null;
const display = value => finite(value) ? value.toLocaleString(undefined, Number.isSafeInteger(value) ? { maximumFractionDigits: 0 } : { maximumSignificantDigits: 6 }) : 'Unavailable';
const triplet = value => ['x', 'y', 'Y'].map(axis => `${axis} ${display(value?.[axis])}`).join(' · ');

function points(table, columns, series) {
  const names = bounded(table?.columns, 12);
  const inputColumn = names.indexOf('input'), indices = columns.map(name => names.indexOf(name));
  if (inputColumn < 0 || indices.some(index => index < 0)) return [];
  const values = new Map(), duplicates = new Set();
  for (const row of bounded(table?.rows, 1024)) {
    if (!Array.isArray(row)) continue;
    const input = row[inputColumn];
    if (!Number.isInteger(input) || input < 0 || input > 255) continue;
    if (values.has(input)) { duplicates.add(input); continue; }
    const samples = indices.map(index => row[index]);
    if (!samples.every(chartFinite)) continue;
    const value = samples.reduce((sum, sample) => sum + sample, 0);
    if (chartFinite(value)) values.set(input, { input, [series]: value });
  }
  return [...values.values()].filter(row => !duplicates.has(row.input)).sort((left, right) => left.input - right.input);
}

function capturedReading(measurements, prefix) {
  const reads = [1, 2, 3].map(index => ({ index, axes: Object.fromEntries(['x', 'y', 'Y'].map(axis => [axis, measurements.get(`${prefix}.read${index}.${axis}`) ?? null])) }));
  const captured = reads.filter(read => Object.values(read.axes).some(finite));
  const latest = captured[captured.length - 1];
  if (!latest) return { measured: { x: null, y: null, Y: null }, readingSource: 'No complete captured read' };
  if (!Object.values(latest.axes).every(finite)) return { measured: latest.axes, readingSource: `Incomplete captured read ${latest.index}` };
  const previous = reads[latest.index - 2];
  if (previous && Object.values(previous.axes).every(finite)) {
    return { measured: Object.fromEntries(['x', 'y', 'Y'].map(axis => [axis, (previous.axes[axis] + latest.axes[axis]) / 2])),
      readingSource: `Mean of captured reads ${previous.index}–${latest.index}` };
  }
  return { measured: latest.axes, readingSource: `Captured read ${latest.index} only` };
}

export function buildNorthstarEvidence(report) {
  const tables = bounded(report?.tables, 12);
  const find = key => tables.find(table => table?.key === key);
  const luminance = {
    native: points(find('native.ramp'), ['Y'], 'native'),
    postLoad: points(find('verification.ramp'), ['Y'], 'postLoad'),
    target: points(find('gamma.target.fill'), ['R', 'G', 'B'], 'target'),
  };
  const compensation = Object.fromEntries([...'RGB'].map(channel => [channel,
    points(find('gamma.compensation'), [channel], channel).filter(row => Number.isInteger(row[channel]) && row[channel] >= 0 && row[channel] <= 255)]));
  const measurements = new Map();
  for (const measurement of bounded(report?.measurements, 10000)) {
    if (typeof measurement?.key === 'string' && measurement.key.length <= 100 && finite(measurement.value)) measurements.set(measurement.key, measurement.value);
  }
  const snapshot = report?.browser?.profileSnapshot;
  const frozen = report?.schemaVersion === 'northstar-pilot/2' && snapshot && Array.isArray(snapshot.ecdis);
  const tokens = new Map();
  if (frozen) for (const token of bounded(snapshot.ecdis, 300)) {
    if (MODES.includes(token?.mode) && typeof token?.name === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(token.name)) tokens.set(`${token.mode}.${token.name}`, token);
  }
  const discovered = new Set();
  for (const key of measurements.keys()) {
    const match = /^ecdis\.(Day|Dusk|Night)\.([A-Za-z0-9_-]{1,64})\./.exec(key);
    if (match) { const identity = `${match[1]}.${match[2]}`; discovered.add(identity); if (!tokens.has(identity) && tokens.size < 300) tokens.set(identity, { mode: match[1], name: match[2] }); }
  }
  const ecdis = [...tokens.entries()].map(([identity, token]) => {
    const prefix = `ecdis.${identity}`, get = suffix => measurements.get(`${prefix}.${suffix}`) ?? null;
    const nominal = Object.fromEntries(['x', 'y', 'Y'].map((axis, index) => [axis, frozen ? numeric(token.xyY?.[index]) : get(`nominal.${axis}`)]));
    const deltaLimit = frozen ? numeric(token.deltaLimit) : get('deltaLimit');
    const candidateWindow = frozen ? token.luminanceWindow : [get('window.low'), get('window.high')];
    const window = Array.isArray(candidateWindow) && candidateWindow.length === 2 && candidateWindow.every(finite)
      && candidateWindow[0] <= candidateWindow[1] ? [...candidateWindow] : null;
    const deltaUvStar = get('delta_uv_star'), luminanceRatio = get('luminance_ratio');
    let comparison = 'Incomplete comparison';
    if (deltaUvStar !== null && luminanceRatio !== null && deltaLimit !== null && window) comparison = deltaUvStar < 0 || luminanceRatio < 0
      ? 'Invalid reported metrics' : deltaUvStar >= deltaLimit || luminanceRatio < window[0] || luminanceRatio > window[1]
        ? 'Reported metrics outside limits' : 'Reported metrics within limits';
    return { mode: token.mode, name: token.name, nominal, ...capturedReading(measurements, prefix), deltaUvStar, deltaLimit, luminanceRatio, window,
      comparison, hasEvidence: discovered.has(identity), limitSource: frozen ? 'Frozen profile' : 'Reported configuration' };
  }).sort((left, right) => MODES.indexOf(left.mode) - MODES.indexOf(right.mode) || left.name.localeCompare(right.name));
  return { luminance, compensation, ecdis };
}

function EvidenceRows({ columns, rows }) {
  const [page, setPage] = useState(0);
  const lastPage = Math.max(0, Math.ceil(rows.length / 100) - 1), current = Math.min(page, lastPage), start = current * 100;
  return <>
    <div className="ni-table-wrap" style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}><table className="ni-table">
      <thead><tr>{columns.map((column, index) => <th key={index}>{column}</th>)}</tr></thead>
      <tbody>{rows.slice(start, start + 100).map((row, index) => <tr key={start + index}>{columns.map((column, offset) => <td key={offset}>
        {typeof row[offset] === 'string' ? row[offset] : display(row[offset])}</td>)}</tr>)}</tbody>
    </table></div>
    {rows.length > 100 && <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginTop: '.5rem' }}>
      <button className="ni-button" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous rows</button>
      <span>Rows {start + 1}–{Math.min(start + 100, rows.length)} of {rows.length}</span>
      <button className="ni-button" disabled={current === lastPage} onClick={() => setPage(current + 1)}>Next rows</button>
    </div>}
  </>;
}

function EvidenceDetails({ title, children }) {
  const [open, setOpen] = useState(false);
  return <details aria-label={title} onToggle={event => setOpen(event.currentTarget.open)} style={{ marginTop: '.75rem' }}><summary style={{ cursor: 'pointer' }}>{title}</summary>
    {open && <div style={{ marginTop: '.5rem' }}>{children}</div>}
  </details>;
}

function CurveChart({ title, series, compensation = false }) {
  const available = series.filter(item => item.data.length);
  if (!available.length) return null;
  return <div style={{ minWidth: 0 }}>
    <h5 style={{ marginBottom: '.5rem', fontSize: '1rem' }}>{title}</h5>
    <div role="img" aria-label={title} style={{ width: '100%', height: 280, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%"><LineChart margin={{ top: 8, right: 15, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="input" type="number" domain={[0, 255]} ticks={[0, 64, 128, 192, 255]} />
        <YAxis domain={compensation ? [0, 255] : ['auto', 'auto']} width={60} />
        <Tooltip formatter={value => display(value)} labelFormatter={value => `Input ${value}`} />
        <Legend />
        {available.map(item => <Line key={item.key} data={item.data} dataKey={item.key} name={item.label} type="linear" stroke={item.color}
          strokeWidth={2} strokeDasharray={item.dashed ? '5 3' : undefined} isAnimationActive={false} connectNulls={false}
          dot={item.data.length <= 33 ? { r: 2 } : false} />)}
      </LineChart></ResponsiveContainer>
    </div>
    <p style={{ color: 'var(--ni-text-muted)', fontSize: '.85rem' }}>Input 0–255 · {compensation ? 'Output code 0–255' : 'Luminance Y in cd/m²'}.
      {' '}Lines connect recorded samples; unrecorded points are not synthesized.</p>
  </div>;
}

export default function NorthstarResults({ report }) {
  const evidence = useMemo(() => buildNorthstarEvidence(report), [report]);
  if (!report || typeof report !== 'object') return null;
  const simulation = report.mode === 'simulation';
  const source = simulation ? 'Synthetic simulation' : report.browser?.acquisition === 'web-serial'
    ? 'Browser instrument capture · client reported' : 'Imported report · client reported';
  const curves = [
    { key: 'native', label: simulation ? 'Native (synthetic)' : 'Native measured', color: '#f59e0b', data: evidence.luminance.native },
    { key: 'postLoad', label: simulation ? 'Post-load (synthetic)' : 'Post-load measured', color: '#3b82f6', data: evidence.luminance.postLoad },
    { key: 'target', label: 'Recorded target (R + G + B)', color: '#10b981', dashed: true, data: evidence.luminance.target },
  ];
  const compensation = [...'RGB'].map((channel, index) => ({ key: channel, label: channel, color: ['#ef4444', '#22c55e', '#3b82f6'][index], data: evidence.compensation[channel] }));
  const hasCurves = curves.some(curve => curve.data.length), hasCompensation = compensation.some(curve => curve.data.length);
  const tables = bounded(report.tables, 12).filter(table => table && typeof table.key === 'string' && Array.isArray(table.columns) && Array.isArray(table.rows));
  const measurements = bounded(report.measurements, 10000).filter(item => item && typeof item.key === 'string');
  return <section aria-label="Northstar Test evidence" style={{ marginTop: '1rem' }}>
    <h4 style={{ marginBottom: '.5rem' }}>Calibration evidence</h4>
    <p>{source} · Test mode. These results do not approve production or ECDIS use.</p>
    {(hasCurves || hasCompensation) && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))', gap: '1rem' }}>
      <CurveChart title="Native and post-load luminance" series={curves} />
      <CurveChart title="RGB compensation" series={compensation} compensation />
    </div>}
    {!hasCurves && !hasCompensation && <p>No recorded curve points are available yet.</p>}
    {evidence.ecdis.length > 0 && <div>
      <h5 style={{ marginBottom: '.5rem', fontSize: '1rem' }}>ECDIS token evidence</h5>
      <p style={{ color: 'var(--ni-text-muted)', fontSize: '.85rem' }}>Nominals and limits come from {report.schemaVersion === 'northstar-pilot/2' ? 'the frozen experimental profile' : 'reported configuration measurements'}.
        {' '}{simulation ? 'Synthetic means' : 'Capture means'} summarize complete raw reads; they do not independently establish settling or instrument qualification.</p>
      {MODES.map(mode => {
        const configured = evidence.ecdis.filter(token => token.mode === mode), rows = configured.filter(token => token.hasEvidence);
        if (!configured.length) return null;
        return <div key={mode} style={{ marginTop: '1rem' }}><h6 style={{ fontSize: '.95rem', margin: '.5rem 0' }}>{mode}</h6>
          {rows.length > 0 && <div className="ni-table-wrap" style={{ overflowX: 'auto' }}><table className="ni-table">
            <thead><tr><th>Token</th><th>{simulation ? 'Synthetic xyY' : 'Captured xyY'} (Y: cd/m²)</th><th>Nominal xyY (Y: cd/m²)</th><th>Δu*v* · configured limit</th><th>Y ratio · configured window</th><th>Reported comparison</th></tr></thead>
            <tbody>{rows.map(token => <tr key={token.name}><td>{token.name}</td>
              <td>{triplet(token.measured)}<div style={{ color: 'var(--ni-text-muted)', fontSize: '.8rem' }}>{simulation
                ? token.readingSource.replace('captured', 'synthetic').replace('Captured', 'Synthetic') : token.readingSource}</div></td>
              <td>{triplet(token.nominal)}</td><td>{display(token.deltaUvStar)}<div>Limit: {token.deltaLimit === null ? 'Unavailable' : `< ${display(token.deltaLimit)}`}</div></td>
              <td>{display(token.luminanceRatio)}<div>Window: {token.window ? `${display(token.window[0])}–${display(token.window[1])}` : 'Unavailable'}</div></td>
              <td>{token.comparison}</td></tr>)}</tbody>
          </table></div>}
          {rows.length < configured.length && <p>{configured.length - rows.length} configured {mode} token(s) have no reported capture yet.</p>}
        </div>;
      })}
    </div>}
    {(tables.length > 0 || measurements.length > 0) && <div style={{ marginTop: '1rem' }}><h5 style={{ fontSize: '1rem', marginBottom: '.5rem' }}>Typed raw evidence</h5>
      <p style={{ color: 'var(--ni-text-muted)', fontSize: '.85rem' }}>Charts omit invalid, out-of-range or duplicate input points. Inspect the recorded tables and measurements below for partial or failed attempts.</p>
      {tables.map((table, index) => {
        const columns = bounded(table.columns, 12).map(column => printable(column));
        const rows = bounded(table.rows, 1024).filter(Array.isArray).map(row => columns.map((column, offset) => numeric(row[offset])));
        return <EvidenceDetails key={`${report.clientAttemptId || ''}:${index}`} title={`${printable(table.key)} · ${rows.length} rows`}>
          <EvidenceRows columns={columns} rows={rows} />
        </EvidenceDetails>;
      })}
      {measurements.length > 0 && <EvidenceDetails key={`${report.clientAttemptId || ''}:measurements`} title={`Raw measurements · ${measurements.length} values`}>
        <EvidenceRows columns={['Measurement', 'Value', 'Unit']} rows={measurements.map(item => [printable(item.key), numeric(item.value), printable(item.unit, 40)])} />
      </EvidenceDetails>}
    </div>}
  </section>;
}
